import React, { useState } from 'react';
import { MessageSquare, Send, AlertCircle, ShieldCheck, User } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';

export const PrescriptionNotesThread = ({ prescriptionId, notes = [], onNoteAdded, readOnly = false }) => {
  const [content, setContent] = useState('');
  const [noteType, setNoteType] = useState('GENERAL');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;

    setSubmitting(true);
    try {
      const res = await api.post(`/api/prescriptions/${prescriptionId}/notes`, {
        content: content.trim(),
        note_type: noteType
      });
      if (res.data?.success) {
        toast.success('Operational note added');
        setContent('');
        setNoteType('GENERAL');
        if (onNoteAdded) {
          onNoteAdded(res.data.data);
        }
      }
    } catch (err) {
      const detail = err.response?.data?.message || err.response?.data?.detail || 'Failed to add note';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-border shadow-sm">
      <CardHeader className="pb-3 border-b border-border/50">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-serif flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            Operational Notes & Communication
          </CardTitle>
          <Badge variant="outline" className="text-xs font-normal">
            {notes.length} {notes.length === 1 ? 'Note' : 'Notes'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-4 space-y-4">
        {/* Notes feed */}
        <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
          {notes.length === 0 ? (
            <div className="text-center py-6 text-xs text-muted-foreground">
              No communication notes on this prescription yet. Notes added here are visible to both doctor and assigned staff.
            </div>
          ) : (
            notes.map((note) => {
              const isAdmin = note.author_role === 'ADMIN';
              const isSystem = note.author_role === 'SYSTEM' || note.note_type === 'SYSTEM';
              const isAlert = note.note_type === 'ALERT';
              const isReason = note.note_type === 'STATUS_REASON';

              return (
                <div 
                  key={note.id} 
                  className={`p-3 rounded-lg text-xs space-y-1.5 border ${
                    isAlert 
                      ? 'bg-amber-50/60 border-amber-200 text-amber-900' 
                      : isReason
                        ? 'bg-blue-50/60 border-blue-200 text-blue-900'
                        : isSystem
                          ? 'bg-muted/40 border-border/50 text-muted-foreground font-mono text-[11px]'
                          : 'bg-card border-border/70 text-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 font-semibold">
                      {isAdmin ? (
                        <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                      ) : (
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      <span>{note.author_name}</span>
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5">
                        {note.author_role}
                      </Badge>
                      {isAlert && (
                        <Badge variant="destructive" className="text-[10px] py-0 px-1.5 flex items-center gap-1">
                          <AlertCircle className="h-2.5 w-2.5" />
                          ALERT
                        </Badge>
                      )}
                    </div>
                    <span className="text-[11px] text-muted-foreground">
                      {new Date(note.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                    </span>
                  </div>

                  <p className="whitespace-pre-wrap leading-relaxed text-xs">
                    {note.content}
                  </p>
                </div>
              );
            })
          )}
        </div>

        {/* Add Note Form */}
        {!readOnly && (
          <form onSubmit={handleSubmit} className="pt-2 border-t border-border/50 space-y-2.5">
            <Textarea
              placeholder="Post an operational note, medicine inquiry, or fulfillment remark..."
              className="min-h-[70px] text-xs resize-none"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground font-medium">Type:</span>
                <button
                  type="button"
                  onClick={() => setNoteType('GENERAL')}
                  className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                    noteType === 'GENERAL' 
                      ? 'bg-primary text-primary-foreground border-primary font-medium' 
                      : 'bg-background hover:bg-muted text-muted-foreground'
                  }`}
                >
                  General
                </button>
                <button
                  type="button"
                  onClick={() => setNoteType('ALERT')}
                  className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                    noteType === 'ALERT' 
                      ? 'bg-amber-600 text-white border-amber-600 font-medium' 
                      : 'bg-background hover:bg-muted text-muted-foreground'
                  }`}
                >
                  Priority Alert
                </button>
              </div>

              <Button type="submit" size="sm" disabled={submitting || !content.trim()} className="h-8 gap-1.5 text-xs">
                <Send className="h-3 w-3" />
                {submitting ? 'Sending...' : 'Post Note'}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default PrescriptionNotesThread;
